import clsx from 'clsx'
import giMark from '@/brand/assets/neoenergia-mark.webp'

/**
 * The customer marks.
 *
 * Two kinds, because the two artworks are two different things and pretending otherwise is
 * how a re-brand ends up approximate.
 *
 * EVT supplied a **logotype**: flat, two-colour, geometric. That vectorises exactly, and it
 * has to — navy on the dark theme's near-black desk is invisible, so the letterforms are
 * painted in `currentColor` and inherit their ink from the surface they sit on. It is traced
 * from the supplied file rather than redrawn, and the trace is checked against it.
 *
 * Neoenergia supplied an **illustration**: three blades whose interiors are continuous 2-D
 * colour fields — green blending through teal into blue across the width while darkening down
 * the height — over a circuit of white traces. No arrangement of SVG gradients reproduces a
 * 2-D field; a linear-gradient approximation of it is a redrawing, not the logo. So the mark
 * ships as the supplied artwork itself, lifted off its white background with a real alpha
 * matte (`scripts/matte-logo.py`), and is exact by construction: recompositing it back onto
 * white reproduces the supplied file to 0.55/255.
 *
 * It needs no theme treatment: it is saturated and self-lit, and reads on the warm desk and
 * the near-black one alike. That is what makes shipping it as pixels rather than paths a
 * trade with nothing on the other side of it.
 */

/** The supplied artwork's own proportions (284×320). Stated so callers can size by height. */
const GI_ASPECT = 284 / 320

/**
 * EVT's logotype, traced from the supplied artwork.
 *
 * The letterforms are `currentColor` so the mark reads on either theme; the slash keeps the
 * brand blue by taking `--primary`, which the EVT profile seeds with it — so the one blue in
 * the app is the one the palette derives everything else from, and they cannot drift apart.
 */
export const EvtWordmark = ({ height = 22, className }: { height?: number; className?: string }) => (
  <svg
    role="img"
    aria-label="EVT"
    viewBox="0 0 190 70"
    height={height}
    width={(height * 190) / 70}
    className={clsx('block', className)}
    fill="none"
  >
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M137.25 68.9C134.25 67.96 134.21 68.19 131.88 66.77C127.14 63.9 123.79 58.59 123.74 53C123.6 35.67 123.72 18.33 123.75 1C123.75 0.7 123.7 0.01 124.24 0C127.78 -0.05 131.34 -0.1 134.88 0.1C135.07 0.11 135.12 0.43 135.12 0.62C135.39 11.84 134.33 7.09 135.38 11.41C139.57 12.82 133.97 11.05 145.75 11.77C146.12 11.79 146.66 12 146.7 12.38C147.02 15.57 146.85 18.79 146.78 22C146.77 22.85 146.31 23.09 145.5 23.12C142.34 23.22 139.16 23.01 136 23.16C135.71 23.18 135.41 23.37 135.27 23.62C135.09 23.96 135.12 24.37 135.12 24.75C135.09 34.21 134.81 43.67 135.17 53.12C135.21 54.21 136.38 56.05 137.37 56.75C137.89 57.12 138.45 57.54 139.09 57.62C141.25 57.92 143.45 57.68 145.62 57.87C145.98 57.9 146.58 57.92 146.62 58.28C146.99 61.54 146.93 64.85 146.78 68.12C146.76 68.52 146.52 69.06 146.12 69.13C144.32 69.45 142.46 69.27 140.62 69.22C139.5 69.19 138.38 69.01 137.25 68.9Z M18.91 54.62C20.19 55.33 21.39 56.2 22.75 56.75C26.74 58.35 31.36 58.22 35.38 56.77C37.8 55.89 38.6 54.7 40.93 53.12C41.09 53.02 41.35 52.87 41.5 53C44.07 55.3 46.5 57.75 48.88 60.25C49.06 60.44 49.13 60.74 49.11 61C49.09 61.27 48.89 61.5 48.77 61.75C48.05 62.38 47.37 63.05 46.61 63.62C43.47 65.97 39.75 67.95 35.88 68.72C33.31 69.24 29.69 69.63 27 69.41C25.32 69.27 23.65 68.98 22 68.64C20.9 68.42 19.79 68.15 18.75 67.73C17.28 67.15 15.86 66.45 14.5 65.65C13.02 64.78 11.55 63.87 10.25 62.75C8.76 61.47 7.41 60.03 6.18 58.5C2.69 54.15 0.43 48.71 -0.02 43.12C-0.2 40.84 -0.1 38.53 0.1 36.25C0.16 35.59 0.59 35.02 0.75 34.37C0.97 33.47 0.9 32.5 1.22 31.62C2.01 29.48 2.93 27.36 4.07 25.38C5.85 22.27 8.76 19.29 11.62 17.21C12.74 16.4 16.85 13.92 19 13.18C22.24 12.08 25.25 11.42 28.75 11.42C31.48 11.42 36.45 12.03 39.25 13.2C42.21 14.43 45.14 16.04 47.62 18.1C53 22.55 56.73 28.96 57.83 35.88C58.2 38.25 58.22 41.77 57.93 44.25C57.88 44.66 57.55 45.01 57.12 45.01C42.71 45.07 28.29 44.91 13.88 45C13.45 45.01 12.99 45.08 12.62 45.31C12.46 45.42 12.47 45.69 12.51 45.88C12.68 46.56 12.91 47.24 13.25 47.85C14.01 49.2 14.81 50.56 15.8 51.75C16.71 52.83 17.87 53.67 18.91 54.62Z M80.31 68C73.58 51.29 66.78 34.61 60.11 17.88C59.6 16.61 59.29 15.27 58.78 14C58.52 13.38 57.61 12.77 58.12 11.93C58.27 11.69 58.6 11.58 58.88 11.57C62.5 11.47 66.13 11.45 69.75 11.61C70.16 11.63 70.71 11.73 70.88 12.11C76.19 24.63 80.92 37.4 86.05 50C86.13 50.19 86.29 50.41 86.5 50.44C86.69 50.47 86.88 50.29 87 50.14C87.28 49.8 87.49 49.41 87.66 49C92.6 36.81 97.12 24.45 102.32 12.38C102.53 11.87 103.21 11.62 103.75 11.59C107.45 11.41 111.18 11.47 114.88 11.76C115.15 11.78 115.24 12.23 115.23 12.5C115.23 12.97 115.04 13.43 114.87 13.88C107.55 32.15 100.12 50.38 92.75 68.64C89 70.81 91.61 69.52 82 69.25C80.96 69.22 80.86 68.73 80.31 68Z M44.12 33.38C44.43 33.03 44.68 32.94 44.38 32.38C42.97 29.77 41.51 28.15 39 26.28C35.25 23.49 30.67 22.7 26.12 23.33C25.07 23.48 24.07 23.86 23.04 24.12C22.11 24.57 20.17 25.42 19.23 26.12C18.24 26.87 17.23 27.6 16.39 28.5C16.22 28.67 13.81 31.46 13.58 32.75C13.46 33.39 14.26 33.44 14.62 33.44C24.46 33.48 34.29 33.4 44.12 33.38Z"
    />
    <path
      fill="var(--primary)"
      d="M166.38 68.88C162.79 68.88 159.21 68.97 155.62 68.89C155.34 68.88 155.03 68.82 154.82 68.62C154.66 68.48 154.57 68.2 154.65 68C161.38 50.83 168.33 33.74 175.2 16.62C176.49 13.42 175.95 14.41 177.62 12.15C181.55 12.19 185.48 12.03 189.39 12.25C189.6 12.26 189.69 12.63 189.62 12.84C189.13 14.47 188.42 16.04 187.78 17.62C181.44 33.51 175.07 49.38 168.67 65.25C167.35 68.54 168.26 67.7 166.38 68.88Z"
    />
  </svg>
)

/**
 * Gi's mark: the supplied artwork, at the size it is being drawn.
 *
 * Sized in CSS pixels and shipped at 320px tall, so the largest placement in the product — the
 * 98px home hero — is still a downscale on a 3× display. Lossless, because "close enough at
 * the size we happen to use today" is how an asset quietly degrades.
 *
 * `width`/`height` are set rather than left to CSS: this is drawn in the rail before anything
 * else has painted, and an image with no intrinsic size reserves none, so the lockup beside it
 * would jump sideways when the file arrives.
 *
 * Decorative, exactly as `Orb` is — every placement sits beside text that already names what
 * it marks, so an empty `alt` keeps it out of the accessibility tree rather than announcing
 * a second copy of the word next to it.
 */
export const GiMark = ({ size = 26, className }: { size?: number; className?: string }) => (
  <img
    src={giMark}
    alt=""
    aria-hidden="true"
    draggable={false}
    decoding="async"
    width={Math.round(size * GI_ASPECT)}
    height={size}
    className={clsx('block select-none', className)}
  />
)
